import React, { type ReactNode } from "react";

export const tooltips: Record<string, ReactNode> = {
  callOpen:
    "Open Interest is the total amount of outstanding contracts. Open Interest = total open long positions = total open short positions.",
  callVolume: "The amount of contracts traded in the last 24 hours.",
  callRho:
    "Rho is a risk measure and gives the sensitivity to the risk-free interest rate.",
  callTheta:
    "Theta is a risk measure and gives the rate of expected price decay over the passage of time.",
  callVega:
    "Vega is a risk measure and gives the USD change in the option price for every 1% change in Implied Volatility.",
  callGamma:
    "Gamma is a risk measure and gives the rate of change in Delta relative to changes in the underlying asset.",
  callDelta:
    "Delta is a risk measure that represents the USD change in option price relative to change in the underlying.",
  callBidSize: "The amount of contracts on the best bid.",
  callIVBid: "The implied volatility of the best bid price.",
  callBid: "The price at which users are willing to buy the option.",
  callMark:
    "This is an exchange metric to estimate the fair value of the option. All user positions are valued against the Mark Price to calculate equity.",
  callAsk: "The price at which users are willing to sell the option.",
  callIVAsk: "The implied volatility of the best ask price.",
  callAskSize: "The number of contracts available at the best ask price.",
  callPosition: "The number of contracts you have for the instrument.",
  strike:
    "The strike price is the predetermined price at which the option holder can choose to exercise the option.",
  putPosition: "The number of contracts you have for the instrument.",
  putBidSize: "The amount of contracts on the best bid.",
  putIVBid: "The implied volatility of the best bid price.",
  putBid: "The price at which users are willing to buy the option.",
  putMark:
    "This is an exchange metric to estimate the fair value of the option. All user positions are valued against the Mark Price to calculate equity.",
  putAsk: "The price at which users are willing to sell the option.",
  putIVAsk: "The implied volatility of the best ask price.",
  putAskSize: "The number of contracts available at the best ask price.",
  putDelta:
    "Delta is a risk measure that represents the USD change in option price relative to change in the underlying.",
  putGamma:
    "Gamma is a risk measure and gives the rate of change in Delta relative to changes in the underlying asset.",
  putVega:
    "Vega is a risk measure and gives the USD change in the option price for every 1% change in Implied Volatility.",
  putTheta:
    "Theta is a risk measure and gives the rate of expected price decay over the passage of time.",
  putRho:
    "Rho is a risk measure and gives the sensitivity to the risk-free interest rate.",
  putVolume: "The amount of contracts traded in the last 24 hours.",
  putOpen:
    "Open Interest is the total amount of outstanding contracts. Open Interest = total open long positions = total open short positions.",
  value:
    "The total value of the position, calculated as size multiplied by the mark price.",
  avgPrice: "The average price paid per instrument unit.",
  markPrice: "The current mark price for this instrument.",
  pnl: "The profit or loss of the position, calculated as the difference between the current value and the cost basis.",
  accruedFunding:
    "Net funding paid or received on this open perpetual position since entry.",
  delta:
    "Delta is a risk measure that represents the USD change in option price relative to change in the underlying.",
  gamma:
    "Gamma is a risk measure and gives the rate of change in Delta relative to changes in the underlying asset.",
  vega: "Vega is a risk measure and gives the USD change in the option price for every 1% change in Implied Volatility.",
  theta:
    "Theta is a risk measure and gives the rate of expected price decay over the passage of time.",
  rho: "Rho is a risk measure and gives the sensitivity to changes in interest rate.",
  limitPrice: "The limit price for this order.",
  filled: "The size already filled from the original order.",
  orderId: "Transaction identification number.",
  tif: "The instructions used when placing the order for how long the order will remain active.",
  data: "The date at which the order was placed.",
  cancel: "Cancel the order.",
  cancelAll: "Cancel all orders for the selected instrument.",
  side: "The direction of the order, sell or buy.",
  price: "The price offered to fill this order.",
  imr: "A value greater than or equal to 100% means you have allocated all your Equity as margin, and hence have no available balance for increasing your position.",
  mmr: "A value greater than or equal to 100% means your account is under liquidation as there is not enough equity to maintain open positions.",
  netPrice: "The net price of the trade after accounting for all adjustments.",
  netPremiums: "Premiums from estimated execution price times leg size",
  "24hch": "The relative change in instrument price over the last 24 hours.",
  "24hhl":
    "The lowest and highest traded price of the instrument over the last 24 hours.",
  "24hv": "The amount of volume traded in the last 24 hours.",
  "%premium":
    "The premium in terms of percentage value of the Perpetual Future over Kyan’s index price.",
  $premium:
    "The premium in terms of dollar value of the Perpetual Future over Kyan’s index price.",
  funding8h:
    "The current payment rate paid from long positions to short positions based on 8h periods.",
  marginUsage:
    "The amount of margin required for opening this position. A negative value reduces total margin usage.",
  availableMargin: "Amount of margin available for opening new positions.",
  FOK: (
    <>
      <strong>Fill or Kill</strong>
      <br />
      Execute a transaction immediately and completely or not at all.
    </>
  ),
  IOC: (
    <>
      <strong>Immediate or Cancel</strong>
      <br />
      Executes the transaction immediately, if any portion of the order cannot
      be filled instantly, it gets cancelled.
    </>
  ),
  postOnly:
    "Post Only orders will always enter the orderbook as maker orders and will be adjusted if they were to be matched instantly.",
  GTC: (
    <>
      <strong>Good Till Cancel</strong>
      <br />
      An unfilled GTC order remains in the orderbook until cancelled, or until
      the option expires.
    </>
  ),
  GTD: (
    <>
      <strong>Good Till Day</strong>
      <br />
      An unfilled GTD order remains in the orderbook until the end of the
      trading day at 0000 UTC.
    </>
  ),
  legPrice: "The average price for this leg of the strategy.",
  legRatio: "The ratio of this leg compared to other legs in the strategy.",
  execPrice: "Based on selected size and current order book liquidity",
  IMr: (
    <>
      <strong>Initial Margin Ratio = Initial Margin / Equity.</strong>
      <br />A value of 100% means you have allocated all your Equity as margin,
      and have no available balance for increasing your position.
    </>
  ),
  MMr: (
    <>
      <strong>Maintenance Margin Ratio = Maintenance Margin / Equity</strong>
      <br />A value of 100% means your account is under liquidation as there is
      not enough equity to maintain open positions.
    </>
  ),
  Delta:
    "A measure of risk across your portfolio that changes dynamically with fluctuations in existing positions and the addition of new positions.",
  krystals:
    "Krystals are distributed based on various factors, and will serve as a marker of those who helped shape Kyan's foundations. Additional rewards will be available soon.",
  withdrawDisabled: "Withdraws are temporarily disabled.",
  score: "Measures overall market making performance and liquidity quality.",
} as const;
